import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ThemeService } from '../../core/theme/theme.service';
import { buildInfo } from '../../core/build-info';

@Component({
  selector: 'app-footer',
  imports: [RouterLink],
  host: {
    '[class.dark]': 'isDark()',
  },
  template: `
    <footer class="site-footer">
      <div class="footer-inner">
        <span class="copyright">&copy; {{ year }} Date Palm Media LLC &mdash; Rebel Paint&trade; is a trademark of Date Palm Media LLC.</span>
        <span class="build-id">Build {{ buildCommit }}</span>
        <nav class="footer-links">
          <a routerLink="/legal/privacy">Privacy</a>
          <a routerLink="/legal/terms">Terms</a>
          <a routerLink="/legal/cookies">Cookies</a>
          <a href="mailto:support&#64;rebelpaint.com">Contact</a>
        </nav>
      </div>
    </footer>
  `,
  styles: `
    :host {
      display: block;
      flex-shrink: 0;
    }

    .site-footer {
      margin-top: 8px;
      padding: 8px 16px;
      border-top: 1px solid #e2e8f0;
      background: transparent;
      font-size: 11px;
    }

    .site-footer.dark {
      border-top-color: #2f3136;
    }

    .footer-inner {
      max-width: 1200px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px 16px;
      flex-wrap: wrap;
    }

    .copyright {
      color: #64748b;
    }

    .build-id {
      color: #64748b;
      font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", monospace;
      white-space: nowrap;
    }

    .site-footer.dark .copyright,
    .site-footer.dark .build-id {
      color: #94a3b8;
    }

    .footer-links {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .footer-links a {
      color: #64748b;
      text-decoration: none;
      /* Expand the tap target to the WCAG minimum. Block (vertical) axis
       * uses padding + an equal negative margin so it doesn't grow the
       * visible footer row height; inline (horizontal) axis grows the box
       * for real via min-inline-size, since these short labels ("Terms")
       * are narrower than 44px and the footer already wraps at narrow
       * widths, so a little extra width per link is harmless. */
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-inline-size: var(--rp-tap-min);
      padding-block: calc((var(--rp-tap-min) - 1em) / 2);
      margin-block: calc((var(--rp-tap-min) - 1em) / -2);
    }

    .footer-links a:hover {
      color: #2563eb;
    }

    .site-footer.dark .footer-links a {
      color: #94a3b8;
    }

    .site-footer.dark .footer-links a:hover {
      color: #60a5fa;
    }
  `
})
export class Footer {
  year = new Date().getFullYear();
  buildCommit = buildInfo.commit;
  private themeService = inject(ThemeService);

  isDark(): boolean {
    return this.themeService.theme() === 'dark';
  }
}
